import React, { useCallback, useEffect, useState } from 'react';
import { Check, Loader2, PlayCircle, PlugZap, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { bapi, fmtDateTime } from '@/lib/billingApi';

const MODE = {
  live: ['Live', 'bg-emerald-100 text-emerald-800'],
  simulator: ['Test mode (simulated gateway)', 'bg-amber-100 text-amber-900'],
  off: ['Card payments off', 'bg-slate-100 text-slate-600'],
};

const Check2 = ({ ok, label }) => (
  <li className="flex items-center gap-2 text-sm text-slate-700">
    {ok ? <Check className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-red-500" />}{label}
  </li>
);

export const GatewayStatusCard = ({ onSwept }) => {
  const [g, setG] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => bapi('/admin/billing/gateway').then(setG).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const test = async () => {
    setBusy('test');
    try {
      const r = await bapi('/admin/billing/gateway/test', { method: 'POST', body: {} });
      (r.ok ? toast.success : toast.error)(r.ok ? `Plug'n Pay connected: ${r.message}` : `Connection failed: ${r.message}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };
  const sweep = async () => {
    setBusy('sweep');
    try {
      const r = await bapi('/admin/billing/sweep', { method: 'POST', body: {} });
      toast.success(`Sweep done: ${Object.entries(r).map(([k, v]) => `${k.replaceAll('_', ' ')} ${v}`).join(' · ')}`);
      load();
      onSwept();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const [label, tone] = MODE[g?.mode] || MODE.off;
  return (
    <Card className="border-slate-200" data-testid="gateway-status-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex flex-wrap items-center gap-2"><PlugZap className="w-5 h-5 text-[#00247D]" />Plug'n Pay gateway
          {g && <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${tone}`} data-testid="gateway-mode">{label}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!g ? <Loader2 className="w-5 h-5 animate-spin text-[#00247D]" /> : (
          <>
            <ul className="grid sm:grid-cols-3 gap-2">
              <Check2 ok={g.account_set} label="Gateway account" />
              <Check2 ok={g.remote_password_set} label="Remote API password" />
              <Check2 ok={g.response_hash_set} label="Response verification hash" />
            </ul>
            <p className="text-xs text-slate-500" data-testid="gateway-last-sweep">
              Last renewal sweep: {g.last_sweep?.last_run_at ? fmtDateTime(g.last_sweep.last_run_at) : 'never'}
              {g.last_sweep?.last_result && ` · ${Object.entries(g.last_sweep.last_result).map(([k, v]) => `${k.replaceAll('_', ' ')} ${v}`).join(' · ')}`}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={test} disabled={!!busy || !g.account_set || !g.remote_password_set} data-testid="gateway-test-btn">
                {busy === 'test' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <PlugZap className="w-4 h-4 mr-1.5" />}Test connection
              </Button>
              <Button size="sm" onClick={sweep} disabled={!!busy} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="gateway-sweep-btn">
                {busy === 'sweep' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1.5" />}Run renewal sweep now
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
